/**
 * Invoice Payment Component
 * Allows applicants to pay final invoices
 */

import React, { useState, useEffect, useCallback, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { invoiceService, InvoiceSummary } from '@/services/invoice.service';
import { stripePromise } from '@/lib/stripe';

interface InvoicePaymentProps {
  invoiceId: string;
}

// Inner component that uses Stripe hooks
const InvoicePaymentInner: React.FC<InvoicePaymentProps> = ({ invoiceId }) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [invoiceSummary, setInvoiceSummary] = useState<InvoiceSummary | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);

  const loadInvoiceAndCreatePayment = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load invoice summary
      const summary = await invoiceService.getInvoice(invoiceId);
      setInvoiceSummary(summary);

      // If balance is due, create payment intent
      if (summary.balance_due_cents > 0) {
        const paymentResponse = await invoiceService.createFinalPayment(invoiceId);
        setClientSecret(paymentResponse.client_secret);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoice');
    } finally {
      setIsLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    loadInvoiceAndCreatePayment();
  }, [loadInvoiceAndCreatePayment]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found');
      setProcessing(false);
      return;
    }

    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            card: cardElement as any,
          }
        }
      );

      if (confirmError) {
        const errorMessage = confirmError.message || 'Payment failed';
        setError(errorMessage);
        setProcessing(false);
      } else if (paymentIntent?.status === 'succeeded') {
        setPaymentSucceeded(true);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      setProcessing(false);
    }
  };

  const formatAmount = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-neutral-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (!invoiceSummary) {
    return (
      <div className="card text-center py-12">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Invoice Not Found</h2>
        <p className="text-neutral-600 mb-6">{error || 'The requested invoice could not be found'}</p>
        <button onClick={() => navigate('/')} className="btn-primary">
          Go Home
        </button>
      </div>
    );
  }

  // Payment succeeded state
  if (paymentSucceeded) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Payment Successful!</h2>
          <p className="text-neutral-600 mb-6">
            Your payment of {formatAmount(invoiceSummary.balance_due_cents)} has been processed successfully.
          </p>
          <div className="space-y-3 mb-6">
            <button
              onClick={() => navigate(`/jobs/${invoiceSummary.invoice.job_id}`)}
              className="btn-primary w-full"
            >
              Back to Job
            </button>
            <button
              onClick={() => navigate('/')}
              className="btn-secondary w-full"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No balance due (refund scenario)
  if (invoiceSummary.balance_due_cents <= 0) {
    const refundAmount = invoiceSummary.deposit_payment
      ? invoiceSummary.deposit_payment.amount_cents - invoiceSummary.invoice.total_amount_cents
      : 0;

    return (
      <div className="max-w-2xl mx-auto">
        <div className="card">
          <h1 className="text-2xl font-bold text-neutral-900 mb-6">Invoice Payment</h1>

          {/* Invoice Summary */}
          <div className="bg-neutral-50 rounded-lg p-6 mb-6">
            <h2 className="font-semibold text-neutral-900 mb-4">Invoice Summary</h2>
            <div className="space-y-3 mb-4">
              {invoiceSummary.invoice.line_items.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-neutral-700">
                    {item.material_type} - {item.quantity.toFixed(2)} tons
                  </span>
                  <span className="font-medium text-neutral-900">{formatAmount(item.total_cents)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-neutral-200 pt-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-700">Invoice Total</span>
                <span className="font-medium">{formatAmount(invoiceSummary.invoice.total_amount_cents)}</span>
              </div>
              {invoiceSummary.deposit_payment && (
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-700">Deposit Applied</span>
                  <span className="font-medium text-green-600">
                    -{formatAmount(invoiceSummary.deposit_payment.amount_cents)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Refund Message */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Payment Required</h3>
            <p className="text-neutral-600 mb-4">
              Your deposit fully covers this invoice.
              {refundAmount > 0 && ` A refund of ${formatAmount(refundAmount)} will be processed shortly.`}
            </p>
            <button
              onClick={() => navigate(`/jobs/${invoiceSummary.invoice.job_id}`)}
              className="btn-primary"
            >
              Back to Job
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Payment form
  return (
    <div className="max-w-2xl mx-auto">
      <div className="card">
        <h1 className="text-2xl font-bold text-neutral-900 mb-6">Invoice Payment</h1>

        {/* Invoice Summary */}
        <div className="bg-neutral-50 rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-neutral-900">Invoice Summary</h2>
            <span className="text-sm text-neutral-600">
              #{invoiceSummary.invoice.invoice_number || invoiceSummary.invoice.id.slice(0, 8)}
            </span>
          </div>

          <div className="space-y-3 mb-4">
            {invoiceSummary.invoice.line_items.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-neutral-700">
                  {item.material_type} - {item.quantity.toFixed(2)} tons @ {formatAmount(item.unit_price_cents)}/ton
                </span>
                <span className="font-medium text-neutral-900">{formatAmount(item.total_cents)}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-neutral-200 pt-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-700">Invoice Total</span>
              <span className="font-medium">{formatAmount(invoiceSummary.invoice.total_amount_cents)}</span>
            </div>
            {invoiceSummary.deposit_payment && (
              <div className="flex justify-between text-sm">
                <span className="text-neutral-700">Deposit Applied</span>
                <span className="font-medium text-green-600">
                  -{formatAmount(invoiceSummary.deposit_payment.amount_cents)}
                </span>
              </div>
            )}
            <div className="flex justify-between font-semibold text-lg pt-2 border-t border-neutral-300">
              <span>Balance Due</span>
              <span className="text-primary-600">{formatAmount(invoiceSummary.balance_due_cents)}</span>
            </div>
          </div>
        </div>

        {/* Payment Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="card-element" className="block text-sm font-medium text-neutral-700 mb-2">
              Card Details
            </label>
            <div className="border border-neutral-300 rounded-lg p-3 bg-white focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-colors">
              <CardElement
                id="card-element"
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#1f2937',
                      '::placeholder': { color: '#9ca3af' }
                    },
                    invalid: {
                      color: '#ef4444'
                    }
                  },
                  hidePostalCode: false
                }}
                onChange={(e: StripeCardElementChangeEvent) => setCardComplete(e.complete)}
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Security Message */}
          <div className="flex items-center justify-center text-xs text-neutral-500 py-2">
            <svg className="w-4 h-4 mr-1.5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Secured by Stripe</span>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!stripe || processing || !cardComplete}
            className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-all flex items-center justify-center ${
              !stripe || processing || !cardComplete
                ? 'bg-neutral-300 cursor-not-allowed'
                : 'bg-accent-600 hover:bg-accent-700 focus:ring-2 focus:ring-accent-500 focus:ring-offset-2'
            }`}
          >
            {processing ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing Payment...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Pay {formatAmount(invoiceSummary.balance_due_cents)}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// Outer component with Stripe Elements provider
export const InvoicePayment: React.FC<InvoicePaymentProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <InvoicePaymentInner {...props} />
    </Elements>
  );
};
